import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import Spinner from '@/components/common/Spinner'
import EditClassModal from '@/components/dashboard/modals/EditClassModal'
import ClassCalendarModal from '@/components/dashboard/modals/ClassCalendarModal'
import CreateClassModal from '@/components/dashboard/modals/CreateClassModal'
import ExportImportClassesModal from '@/components/dashboard/modals/ExportImportClassesModal'
import { isAdminRole } from '@/utils/roles'

export default function ClassesList({ open = false, onToggle }: { open?: boolean; onToggle?: () => void }) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [classes, setClasses] = useState<ClassResponse[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<ClassResponse | null>(null)
  const [calendarFor, setCalendarFor] = useState<ClassResponse | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showExportImport, setShowExportImport] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  // Teachers manage only the classes they're assigned to; creating/importing/deleting is Admin-only.
  const isAdmin = isAdminRole(user?.role)

  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!user?.parish_id) return
      setLoading(true)
      try {
        const data = await classesApi.list()
        const all = Array.isArray(data) ? data : []
        if (mounted) setClasses(isAdmin ? all : all.filter(c => c.teacher_ids?.includes(user.id)))
      } catch {
        if (mounted) setClasses([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [user?.parish_id])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return classes
    return classes.filter(c =>
      `${c.name} ${c.address ?? ''} ${c.class_id ?? ''} ${(c.teacher_names ?? []).join(' ')}`.toLowerCase().includes(q))
  }, [classes, query])

  const handleDelete = async (classId: string, className: string) => {
    if (deleteConfirm !== 'delete') {
      addToast({ message: 'Please type "delete" to confirm', type: 'error' })
      return
    }

    try {
      await classesApi.remove(classId)
      addToast({ message: `Class "${className}" deleted successfully`, type: 'success' })
      setDeleting(null)
      setDeleteConfirm('')
      // Refresh the list
      const data = await classesApi.list()
      setClasses(Array.isArray(data) ? data : [])
    } catch (error: any) {
      console.error('Failed to delete class:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to delete class',
        type: 'error',
      })
    }
  }

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-busy={loading}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 focus-ring rounded px-1 -ml-1">
          <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
          Classes
        </button>
        <div className="flex items-center gap-2">
          <input
            className="input-field w-64 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
            aria-label="Search classes"
            placeholder="Search classes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {isAdmin && (<>
          <button
            className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            onClick={() => setShowExportImport(true)}
            title="Export classes to JSON files, import from files, or search for previous classes to use as templates"
          >
            📤 Export / Import / Search
          </button>
          <button
            className="btn-primary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            onClick={() => setShowCreate(true)}
            title="Create a new class for your catechism program. You can add events, assign students, and manage the schedule."
          >
            + Create Class
          </button>
          </>)}
        </div>
      </div>
      {open && (<>
      {loading && <Spinner label="Loading classes…" />}
      {!loading && (
        filtered.length === 0 ? (
          <div className="text-sm text-gray-500">{isAdmin ? 'No classes found.' : 'You are not assigned to any classes yet. An Admin assigns teachers to classes.'}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(c => (
              <div key={c.id} className="py-3 flex items-center justify-between">
                <div className="flex-1">
                  <Link
                    to={`/classes/${c.id}`}
                    className="font-medium hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 rounded"
                  >
                    {c.name}
                  </Link>
                  <div className="text-xs text-gray-600">
                    {c.address || 'No location'} • {c.weekly_time_description || 'No weekly time'}
                    {c.start_date && <> • Starts {new Date(c.start_date).toLocaleDateString()}</>}
                  </div>
                  <div className="text-xs text-gray-500">
                    {c.class_id} • Teachers: {c.teacher_names?.length ? c.teacher_names.join(', ') : <span className="text-red-600">none assigned</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-secondary text-xs" onClick={() => setEditing(c)} title="Edit class details, notes, location, and weekly time">Edit</button>
                  <button className="btn-secondary text-xs" onClick={() => setCalendarFor(c)} title="View and manage the class calendar with events, holidays, and student birthdays">Calendar</button>
                  {!isAdmin ? null : deleting === c.id ? (
                    <div className="flex items-center gap-2 border-2 border-red-300 bg-red-50 rounded px-3 py-1">
                      <input
                        className="text-xs border-red-300 rounded focus:outline-none focus:ring-2 focus:ring-red-400/60 w-24"
                        placeholder="Type 'delete'"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        autoFocus
                        disabled={deleting !== c.id}
                      />
                      <button
                        className="btn-secondary text-xs bg-red-600 text-white hover:bg-red-700 focus-ring"
                        onClick={() => handleDelete(c.id, c.name)}
                        disabled={deleting !== c.id}
                      >
                        Delete
                      </button>
                      <button
                        className="btn-secondary text-xs focus-ring"
                        onClick={() => {
                          setDeleting(null)
                          setDeleteConfirm('')
                        }}
                        disabled={deleting !== c.id}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-secondary text-xs text-red-600 hover:text-red-800 focus-ring"
                      onClick={() => setDeleting(c.id)}
                      aria-label={`Delete ${c.name}`}
                      title="Delete this class"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
      </>)}
      <EditClassModal
        open={!!editing}
        onClose={() => setEditing(null)}
        cls={editing ?? { id: '', name: '' }}
        onSaved={(updated) => {
          // EditClassModal already saved; show the server's version (includes teacher names)
          setClasses((prev) => prev.map(c => c.id === updated.id ? updated : c))
        }}
      />
      <ClassCalendarModal
        open={!!calendarFor}
        onClose={() => setCalendarFor(null)}
        cls={{ id: calendarFor?.id ?? '', name: calendarFor?.name ?? '' }}
      />
      <CreateClassModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={async () => {
          // Refresh the list
          try {
            const data = await classesApi.list()
            setClasses(Array.isArray(data) ? data : [])
            // Dispatch event to notify other components (like AutoRegistrationRules) that classes have been updated
            window.dispatchEvent(new CustomEvent('classes-updated'))
          } catch {
            // Ignore error, will show on next manual refresh
          }
        }}
      />
      <ExportImportClassesModal
        open={showExportImport}
        onClose={() => setShowExportImport(false)}
        classes={classes}
        onClassCreated={async () => {
          // Refresh the list
          try {
            const data = await classesApi.list()
            setClasses(Array.isArray(data) ? data : [])
            // Dispatch event to notify other components (like AutoRegistrationRules) that classes have been updated
            window.dispatchEvent(new CustomEvent('classes-updated'))
          } catch {
            // Ignore error, will show on next manual refresh
          }
        }}
      />
    </motion.div>
  )
}


